/**
 * Discover sampling rule templates (discover-api).
 */
import { API } from '../Auth';

const EP = 'discoverRuns';
const BASE = '/api/v1/discover/sampling-rules';

export async function listSamplingRuleTemplates() {
  const rows = await API.get(EP, BASE);
  return Array.isArray(rows) ? rows : [];
}

export async function createSamplingRuleTemplate(body) {
  return API.post(EP, BASE, { body });
}

export async function updateSamplingRuleTemplate(id, body) {
  return API.put(EP, `${BASE}/${encodeURIComponent(id)}`, { body });
}

export async function deleteSamplingRuleTemplate(id) {
  await API.del(EP, `${BASE}/${encodeURIComponent(id)}`);
}

/** @param {object} dto - SamplingRuleTemplateResponseDTO */
export function templateResponseToUiRule(dto) {
  if (!dto) return null;
  const pct = dto.samplePercent != null ? Number(dto.samplePercent) : 3;
  // Reconstruct size/every for the editor. Scale `every` up for sub-0.1% rates so `size` stays a
  // meaningful integer (with every=1000, a 0.01% rate rounds size to 0 and collapses to the
  // default). every = ceil(100 / pct) guarantees size ≥ 1 for any pct the engine accepts.
  const every = pct > 0 && pct < 0.1 ? Math.ceil(100 / pct) : 1000;
  const size = Math.max(1, Math.min(every, Math.round((pct / 100) * every) || 30));
  return {
    id: `preset-${dto.id}`,
    samplingRuleTemplateId: dto.id,
    name: dto.name,
    dry_run: false,
    short_circuit_if: {},
    exclude_if: { column: {} },
    search_all_if: {},
    sample: {
      row: { every, size, active: true },
      column: { every: 500, size: 40, active: true },
    },
    // maxRowsPerTable is no longer a per-preset concern — the per-table row cap is a single global
    // set in the Advanced Config console (AdvancedConfigSettings.maxRowsPerTable) and enforced by the
    // worker. Presets carry sample percent only.
    builtIn: dto.builtIn === true,
    last: false,
  };
}

/** @param {object} rule - UI rules object from RulesEditor */
export function uiRuleToTemplateRequest(rule) {
  const name = (rule && rule.name) || 'Sampling rule';
  let samplePercent = 10;
  if (rule?.sample?.row?.size > 0 && rule?.sample?.row?.every > 0) {
    const raw = (rule.sample.row.size / rule.sample.row.every) * 100;
    // Floor at 0.001% (see buildDiscoveryRequest): the engine supports the .01/.001 percent rates
    // the old 0.1% floor + 1-decimal rounding gated out. Round to 3 decimals to preserve them.
    samplePercent = Math.min(100, Math.max(0.001, Math.round(raw * 1000) / 1000));
  }
  // No maxRowsPerTable: the per-table row cap is a single global (Advanced Config), not per-preset.
  const body = { name, samplePercent };
  return body;
}

export function parsePresetTemplateId(ruleId) {
  if (ruleId == null) return null;
  if (typeof ruleId === 'number' && Number.isFinite(ruleId)) return ruleId;
  const s = String(ruleId);
  if (s.startsWith('preset-')) {
    const n = Number(s.slice(7));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Full replace: upsert each rule as a template; delete server templates not in list (except builtIn).
 * @param {Array<object>} rules - rules list from UI
 */
export async function saveRulesList(rules = []) {
  const serverList = await listSamplingRuleTemplates();
  const serverIds = new Set(serverList.map((t) => t.id));
  // Only match by name against user-owned server templates. Built-ins live at
  // userId=null and aren't updatable via PUT (the service requires ownership),
  // so any apparent "match" against a built-in would result in a silent 404.
  const serverByName = new Map(
    serverList.filter((t) => !t.builtIn).map((t) => [t.name, t])
  );

  const clientTemplateIds = new Set();
  for (const rule of rules) {
    if (rule.builtIn === true) {
      // Built-in presets are seeded by the server and read-only from the UI.
      // Skip — the cleanup loop below also preserves them.
      continue;
    }
    let tid = rule.samplingRuleTemplateId ?? parsePresetTemplateId(rule.id);
    if (tid == null && rule.name && serverByName.has(rule.name)) {
      tid = serverByName.get(rule.name).id;
    }
    const req = uiRuleToTemplateRequest(rule);
    if (tid != null && serverIds.has(tid)) {
      await updateSamplingRuleTemplate(tid, req);
      clientTemplateIds.add(tid);
    } else {
      const created = await createSamplingRuleTemplate(req);
      if (created && created.id != null) {
        clientTemplateIds.add(created.id);
      }
    }
  }

  for (const t of serverList) {
    if (!t.builtIn && !clientTemplateIds.has(t.id)) {
      await deleteSamplingRuleTemplate(t.id);
    }
  }
}
